import type { SettingListConfig } from "@/types/setting-list-config.types";

export type TelliAgent = {
    id: string;
    title: string;
    language: string;
    createdAt: string;
};

export type TelliContact = {
    id: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string | null;
    externalId: string | null;
};

export type PredefinedTelliContact = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
};

export type TelliContactsPage = {
    data: TelliContact[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
};

export type TriggerCallStatus = "idle" | "loading" | "success" | "error";

export type TriggerCallResult = {
    status: TriggerCallStatus;
    callId?: string;
    message?: string;
    triggeredAt?: string;
};

export type InitiateTelliCallPayload = {
    contactId: string;
    agentId: string;
};

export type InitiateTelliCallResponse = {
    message: string;
    callId: string;
};

export type CreateTelliContactPayload = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    externalId: string;
};

export const TELLI_CALL_STATE = {
    QUEUED: "queued",
    RINGING: "ringing",
    IN_PROGRESS: "in_progress",
    PROCESSING: "processing",
    ENDED: "ended",
} as const;

export type TelliCallState = (typeof TELLI_CALL_STATE)[keyof typeof TELLI_CALL_STATE];

export type TelliCallConnectionStatus = "connected" | "not_connected" | "voicemail" | "failed";

export type TelliCallDirection = "outbound" | "inbound";

export type TelliCall = {
    id: string;
    fromNumber: string;
    toNumber: string;
    direction: TelliCallDirection;
    contactId: string;
    agentId: string;
    state: TelliCallState;
    status: TelliCallConnectionStatus | null;
    durationSeconds: number | null;
    triggeredAt: string;
    startedAt: string | null;
    endedAt: string | null;
};

export type TelliCallDetail = TelliCall & {
    endedReason: string | null;
    attempt: number;
    durationMinutes: number | null;
    callStatus: string;
    recordingUrl: string | null;
};

export type TelliCallsPage = {
    data: TelliCall[];
    pagination: {
        page: number;
        limit: number;
        count: number;
        hasMore: boolean;
    };
};

export type TelliWorkerLeadAssignation = {
    priorities: number[];
    listConfig: SettingListConfig;
};

/** Lead status → Telli agent id. Missing / undefined means ignore leads with that status. */
export type TelliWorkerAgentRouting = {
    byStatus: Record<string, string | undefined>;
};

export type TelliWorkerUserAssignation = {
    userId: string | undefined;
};

export const TELLI_WORKER_CONFIG_SECTIONS = {
    USER_ASSIGNATION: "user_assignation",
    AGENT_ROUTING: "agent_routing",
    LEAD_ASSIGNATION: "lead_assignation",
    CALL_ATTEMPTS: "call_attempts",
} as const;

export type TelliWorkerConfigSection =
    (typeof TELLI_WORKER_CONFIG_SECTIONS)[keyof typeof TELLI_WORKER_CONFIG_SECTIONS];

export type TelliWorkerConfigChecklistItem = {
    id: TelliWorkerConfigSection;
    label: string;
    description: string;
    ready: boolean;
    hasUnsavedChanges: boolean;
};

export type TelliWorkerConfig = {
    enabled: boolean;
    leadAssignation: TelliWorkerLeadAssignation;
    agentRouting: TelliWorkerAgentRouting;
    userAssignation: TelliWorkerUserAssignation;
    maxAttempts: number;
};

export const TELLI_WORKER_QUEUE_ITEM_STATUS = {
    QUEUED: "queued",
    CONTACT_CREATED: "contact_created",
    CALL_INITIATED: "call_initiated",
    RINGING: "ringing",
    COMPLETED: "completed",
    RESOLVED: "resolved",
    FAILED: "failed",
    SKIPPED: "skipped",
} as const;

export type TelliWorkerQueueItemStatus =
    (typeof TELLI_WORKER_QUEUE_ITEM_STATUS)[keyof typeof TELLI_WORKER_QUEUE_ITEM_STATUS];

export type TelliWorkerQueueStatusEvent = {
    status: TelliWorkerQueueItemStatus;
    at: string;
};

export type TelliWorkerQueueItem = {
    leadId: string;
    zohoLeadId: string;
    fullName: string;
    phone: string;
    email?: string;
    status: string;
    language: string;
    layout: string | null;
    settingPriority: number | null;
    contactId?: string;
    agentId?: string;
    callId?: string;
    attemptCount: number;
    itemStatus: TelliWorkerQueueItemStatus;
    statusHistory: TelliWorkerQueueStatusEvent[];
    error?: string;
};

export type TelliWorkerCallHistoryItem = {
    callId: string;
    leadId: string;
    zohoLeadId: string;
    fullName: string;
    phone: string;
    agentId: string | null;
    leadStatus: string;
    callResult: string;
    connectionStatus: string | null;
    durationSeconds: number | null;
    recordingUrl: string | null;
    startedAt: string | null;
    endedAt: string | null;
    finishedAt: string;
    attemptNumber: number;
    maxAttempts: number;
};

export type TelliWorkerStatus = {
    enabled: boolean;
    assigneeId: string;
    startedBy: string;
    filter: {
        languages: string[];
        layouts?: string[];
        statuses?: string[];
        settingPriorityMin?: number;
        settingPriorityMax?: number;
    };
    priorities: number[];
    agentRouting: TelliWorkerAgentRouting;
    maxAttempts: number;
    queue: TelliWorkerQueueItem[];
    callHistory: TelliWorkerCallHistoryItem[];
    nextLeadId: string | null;
    activeCallId: string | null;
    activeLeadId: string | null;
    lastError: string | null;
};

export type StartTelliWorkerPayload = {
    assigneeId: string;
    filter: TelliWorkerStatus["filter"];
    priorities: number[];
    agentRouting: TelliWorkerAgentRouting;
    maxAttempts: number;
};
